import { apiSlice, APITAGS, ErrorType } from '../../apis/api';
import { CalculationsCollection } from '../../swagger/collections';
import {
  CalculationDetailResponse,
  CalculationFileType,
  CalculationPickingExecutionDetailsResponse,
  CalculationStatus,
  CalculationType,
  CalculationUpdateRequest,
  DataRetrievalStatus,
  PapProgressStatus,
  PapSyncStatus,
  PickingPositionResponse,
  SkuRedistributionResponse,
} from '@ydistri/api-sdk';
import { apiCalculations } from '../Calculations/apiCalculations';
import { getTags } from '../../apis/apiLib';
import {
  SignalCalculationFileStatusChanged,
  SignalCalculationStatusChanged,
} from '../Calculations/calculationsTypes';
import { signalrClient } from '../../signalr/client';
import { addToast } from '@ydistri/utils';
import {
  SignalCalculationFlagChanged,
  SignalConfigurationChanged,
} from '../../signalr/signalrInterfaces';
import { ROOT_CATEGORY_ID, YPICK_LABEL } from '../../components/global/_constants';
import { createSinglePickingPositionConditions } from './apiCalculationDetailLib';
import { getSelectedProjectShortName } from '../../apis/apiHeader';

const { TAGS, TAGS_ARRAY } = getTags('calculationDetail');

export interface GetSinglePickingPositionRequestParams {
  calculationId: number;
  sourceSkuId: number;
  targetSkuId: number;
}

export interface GetPickingPositionsWithinProductGroupRequestParams {
  calculationId: number;
  sourceSkuId: number;
  targetSkuId: number;
  productGroupId: number;
}

export interface CalculationUpdateRequestParams {
  calculationId: number;
  updateRequest: CalculationUpdateRequest;
}

export interface SkuRedistributionResponseDataResponse {
  messages?: string[] | null;
  data: SkuRedistributionResponse | undefined;
}

export interface PickingPositionExecutionRequest {
  calculationId: number;
  sourceSkuId: number;
  targetSkuId: number;
}

export const apiCalculationDetail = apiSlice
  .enhanceEndpoints({ addTagTypes: TAGS_ARRAY })
  .injectEndpoints({
    endpoints: builder => ({
      getCalculation: builder.query<CalculationDetailResponse, number>({
        queryFn: async calculationId => {
          const { data } = await CalculationsCollection.getCalculation(calculationId);
          return data;
        },
        providesTags: result => [{ type: TAGS.calculation, id: result?.id ?? 'undefined' }],
        async onCacheEntryAdded(calculationId, api) {
          const projectShortName = getSelectedProjectShortName();
          await api.cacheDataLoaded;

          const cacheEntry = api.getCacheEntry();
          if (cacheEntry.data) {
            const listener = (signal: SignalCalculationFileStatusChanged) => {
              if (signal.calculationId === calculationId) {
                //when a file was saved we fetch the calculation to replace the
                //files array with a new one that contains the new file and possibly another
                //file that is computing as we have no way of knowing whether the saved file
                //was the last one
                if (signal.dataRetrievalStatus === DataRetrievalStatus.Saved) {
                  CalculationsCollection.getCalculation(calculationId)
                    .then(response => {
                      const files = response.data.data.files;
                      api.updateCachedData(draft => {
                        draft.files = files;
                      });
                      const file = files.find(f => f.id === signal.fileId);
                      if (file) {
                        api.dispatch(
                          addToast({
                            message: `Calculation ${
                              file.calculationFileType === CalculationFileType.PairingXlsx
                                ? 'XLSX'
                                : 'ERP'
                            } file is ready`,
                          }),
                        );
                      }
                    })
                    .catch((error: unknown) => {
                      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- we know the type
                      const err = error as ErrorType;
                      // eslint-disable-next-line no-console -- we want the output here
                      console.error('Error when retrieving list of files', err);
                      api.dispatch(
                        addToast({
                          message:
                            'There was an error when retrieving list of files. Please reload the page',
                          isError: true,
                        }),
                      );
                    });
                }
              }
            };

            const listener2 = (signal: SignalConfigurationChanged) => {
              // eslint-disable-next-line no-console -- we want the output here
              console.log('SIGNAL! SignalConfigurationChanged', signal);
              api.updateCachedData(draft => {
                if (draft.id !== signal.templateId) return;

                signal.updates.forEach(u => {
                  if (u.value) {
                    switch (u.fieldName) {
                      case 'name':
                        draft.title = u.value;
                        break;
                      case 'description':
                        draft.description = u.value;
                        break;
                      case 'isPap':
                        draft.isPap = u.value === '1';
                        break;
                      case 'isSkuRedistributionEdited':
                        draft.isSkuRedistributionEdited = u.value === '1';
                        break;
                      case 'isBackedUp':
                        draft.isBackedUp = u.value === '1';
                        break;
                      case 'papSyncStatus':
                        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- we know the type
                        draft.papDbSyncStatus = u.value as PapSyncStatus;

                        //if synced, refetch calculation data to get new "synced" time
                        if (draft.papDbSyncStatus === PapSyncStatus.Synced) {
                          api.dispatch(
                            addToast({
                              message: `Calculation ${draft.id} synced to ${YPICK_LABEL}. Refreshing data.`,
                            }),
                          );

                          api.dispatch(
                            apiCalculationDetail.util.invalidateTags([
                              { type: TAGS.calculation, id: draft.id },
                            ]),
                          );
                          return;
                        }
                        break;
                      case 'papResultCalculationProgressStatus':
                        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- we know the type
                        draft.papResultCalculationProgressStatus = u.value as PapProgressStatus;
                        break;
                      case 'calculationTypeId':
                        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- we know the type
                        draft.type = u.value as CalculationType;
                        break;
                      case 'finishExecutionUntil':
                        draft.finishExecutionUntil = u.value;
                        break;
                    }
                  }
                });
              });
            };

            const listener3 = (signal: SignalCalculationStatusChanged) => {
              if (
                signal.projectSlug !== projectShortName ||
                getSelectedProjectShortName() !== projectShortName ||
                signal.calculationId !== calculationId
              )
                return;

              api.updateCachedData(draft => {
                draft.status = signal.status;
              });

              if (signal.status !== CalculationStatus.Completed) return;

              // Manual imports finish asynchronously, after their confirmation request resolves.
              api.dispatch(
                apiSlice.util.invalidateTags([
                  APITAGS.detail.skus,
                  APITAGS.detail.productSizes,
                  APITAGS.detail.skuEntityLists,
                  APITAGS.detail.skuCalculationsSource,
                  APITAGS.detail.skuCalculationsTarget,
                  APITAGS.calculationSkus.skuAttributes,
                  APITAGS.calculationSkus.calculationSkus,
                  APITAGS.calculationCategories.calculationCategoryPairings,
                  APITAGS.entityListsAdministration.entityListItems,
                ]),
              );
            };

            // A calculation flag settled server-side - today that is the deferred SkuPotential debug
            // import finishing or crashing, long after the calculation itself reported Completed.
            // The flag lives on the server and gates the debug-dependent screens, so refetch rather
            // than patching the draft: the signal carries the one flag that changed, not the
            // resulting flag set.
            const listener4 = (signal: SignalCalculationFlagChanged) => {
              if (signal.calculationId !== calculationId) return;

              api.dispatch(
                apiCalculationDetail.util.invalidateTags([
                  { type: TAGS.calculation, id: calculationId },
                ]),
              );
            };

            signalrClient.on('CalculationFileStatusChanged', listener);
            signalrClient.on('TemplateConfigurationChanged', listener2);
            signalrClient.on('CalculationStatusChanged', listener3);
            signalrClient.on('CalculationFlagChanged', listener4);

            api.cacheEntryRemoved.then(() => {
              signalrClient.off('CalculationFileStatusChanged', listener);
              signalrClient.off('TemplateConfigurationChanged', listener2);
              signalrClient.off('CalculationStatusChanged', listener3);
              signalrClient.off('CalculationFlagChanged', listener4);
            });
          }

          await api.cacheEntryRemoved;
        },
      }),
      getSinglePickingPosition: builder.query<
        SkuRedistributionResponse | undefined,
        GetSinglePickingPositionRequestParams
      >({
        queryFn: async ({ calculationId, sourceSkuId, targetSkuId }) => {
          const { data } = await CalculationsCollection.getCalculationCategoryPairings(
            calculationId,
            ROOT_CATEGORY_ID,
            {
              conditions: createSinglePickingPositionConditions(sourceSkuId, targetSkuId),
            },
          );

          const result: SkuRedistributionResponseDataResponse = {
            data: data.data.length > 0 ? data.data[0] : undefined,
          };

          return result;
        },
        providesTags: (result, e, arg) => [
          { type: TAGS.calculationSinglePickingPosition, id: JSON.stringify(arg) },
        ],
      }),
      getPickingPositionsWithinProductGroup: builder.query<
        PickingPositionResponse[] | undefined,
        GetPickingPositionsWithinProductGroupRequestParams
      >({
        queryFn: async ({ calculationId, sourceSkuId, targetSkuId, productGroupId }) => {
          const { data } = await CalculationsCollection.getPickingPositionsWithinProductGroup(
            calculationId,
            sourceSkuId,
            targetSkuId,
            productGroupId,
          );

          return {
            data: data.data.length > 0 ? data.data : [],
          };
        },
        providesTags: (result, e, arg) => [
          { type: TAGS.calculationPickingPositionWithinProductGroup, id: JSON.stringify(arg) },
        ],
      }),
      updateCalculation: builder.mutation<
        CalculationDetailResponse,
        CalculationUpdateRequestParams
      >({
        queryFn: async ({ calculationId, updateRequest }) => {
          const response = await CalculationsCollection.patchCalculation(
            calculationId,
            updateRequest,
          );
          return { data: response.data.data };
        },
        onQueryStarted: ({ calculationId, updateRequest }, { dispatch, queryFulfilled }) => {
          const patchResult = dispatch(
            apiCalculationDetail.util.updateQueryData('getCalculation', calculationId, draft => {
              const requestCopy = { ...updateRequest };
              const newType = requestCopy.calculationType ?? draft.type;
              if ('calculationType' in requestCopy) delete requestCopy.calculationType;
              // Custom export starts now use dedicated run endpoints; ignore stale legacy callers.
              Object.assign(draft, { type: newType, ...requestCopy });
            }),
          );

          //we need to invalidate the list of calculations after the update
          //so when user returns to the list of calculations, the list is refreshed with new type, name or description
          queryFulfilled
            .then(() => {
              dispatch(
                apiCalculations.util.invalidateTags([
                  { type: APITAGS.calculations.calculations, id: calculationId },
                  { type: APITAGS.calculations.calculations, id: 'LIST' },
                ]),
              );
            })
            .catch(patchResult.undo);
        },
      }),
      getCalculationPickingPositionExecution: builder.query<
        CalculationPickingExecutionDetailsResponse | undefined,
        PickingPositionExecutionRequest
      >({
        queryFn: async ({ calculationId, sourceSkuId, targetSkuId }) => {
          const response = await CalculationsCollection.getCalculationStatisticsExecutionDetails(
            calculationId,
            sourceSkuId,
            targetSkuId,
          );

          if (response.status === 204) {
            return { data: undefined };
          }

          return { data: response.data.data };
        },
        providesTags: (result, e, arg) => [
          { type: TAGS.calculationPickingPositionExecution, id: JSON.stringify(arg) },
        ],
      }),
    }),
  });

export const {
  useGetCalculationQuery,
  useUpdateCalculationMutation,
  useGetSinglePickingPositionQuery,
  useGetPickingPositionsWithinProductGroupQuery,
  useGetCalculationPickingPositionExecutionQuery,
} = apiCalculationDetail;
